import { useQuery, UseQueryOptions } from 'react-query';

import { fetchJson } from '@zapier/toolbox-browser-fetch';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { useNotify } from 'app/developer-v3/hooks';

import {
  Issue,
  formatResponseBody as formatIssueResponseBody,
} from './useIssue';

type Args = {
  actions: string[];
  integrationId: number;
  orderBy: 'created_at' | 'priority';
  orderDirection: 'ASC' | 'DESC';
};

export type IssueItem = Omit<Issue, 'comments' | 'events' | 'stream'>;

export function useQueryIntegrationIssuesAll(
  { actions, integrationId, orderBy, orderDirection }: Args,
  options?: UseQueryOptions<IssueItem[], Error, IssueItem[]>
) {
  const notify = useNotify();

  const url = new URL(
    `api/platform/cli/apps/${encodeURIComponent(integrationId)}/issues`,
    ZAPIER_APP_BASE_URL
  );
  url.searchParams.set('page', '1');
  if (actions.length > 0) {
    url.searchParams.set('action', actions.join(','));
  }
  if (orderBy) {
    url.searchParams.set(
      'ordering',
      orderDirection === 'DESC' ? `-${orderBy}` : orderBy
    );
  }

  const query = useQuery({
    queryKey: url.href,
    queryFn: async () => {
      const data = await fetchJsonPaginated(url.href);
      return data;
    },
    onError: async error => {
      notify.failure(error.message, url.href);
    },
    ...options,
  });

  return query;
}

async function fetchJsonPaginated(url: string): Promise<IssueItem[]> {
  const firstUrl = new URL(url);
  let response = await fetchJson(url);
  const results: IssueItem[] = response.results.map(formatIssueResponseBody);
  // We should just be able to use the `next` URL, but it's broken in local
  // development. Instead we need to manually update the `page` parameter of the
  // initial URL.
  while (response.next_page_index) {
    const nextUrl = new URL(firstUrl.href);
    nextUrl.searchParams.set('page', String(response.next_page_index));
    response = await fetchJson(nextUrl.href);
    results.push(...response.results.map(formatIssueResponseBody));
  }
  return results;
}
